import { ROUTE_IDS } from "./routeIds";
import { PRODUCT_IDS } from "./productContext";

/**
 * Single source of truth for route metadata and path segments.
 * Path resolution: routeGuards.resolveRoutePath() reads paths from here only.
 *
 * @typedef {Object} RouteDefinition
 * @property {string} id
 * @property {import('./productContext').ProductId} product
 * @property {string} feature
 * @property {string} capability
 * @property {string} namespace
 * @property {Record<string, string|null|undefined>} paths — segment after dashboard/public base
 */

/** @type {Record<string, RouteDefinition>} */
export const ROUTE_REGISTRY = Object.freeze({
  [ROUTE_IDS.LOGIN]: Object.freeze({
    id: ROUTE_IDS.LOGIN,
    product: PRODUCT_IDS.PUBLIC,
    feature: "auth",
    capability: "dashboard",
    namespace: "auth",
    paths: Object.freeze({
      he: "login",
      en: "login",
      el: "login",
    }),
  }),

  [ROUTE_IDS.PASSWORD_RESET]: Object.freeze({
    id: ROUTE_IDS.PASSWORD_RESET,
    product: PRODUCT_IDS.PUBLIC,
    feature: "auth",
    capability: "dashboard",
    namespace: "auth",
    paths: Object.freeze({
      he: "passwordreset",
      en: "passwordreset",
      el: "passwordreset",
    }),
  }),

  [ROUTE_IDS.DASHBOARD_HOME]: Object.freeze({
    id: ROUTE_IDS.DASHBOARD_HOME,
    product: PRODUCT_IDS.AGENT,
    feature: "dashboard",
    capability: "dashboard",
    namespace: "dashboard",
    paths: Object.freeze({
      he: "home",
      en: "home",
      el: "home",
    }),
  }),

  [ROUTE_IDS.HOTEL_SEARCH]: Object.freeze({
    id: ROUTE_IDS.HOTEL_SEARCH,
    product: PRODUCT_IDS.AGENT,
    feature: "hotel_search",
    capability: "hotels",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchhotels",
      en: "hotels/search",
      el: "hotels/search",
    }),
  }),

  [ROUTE_IDS.HOTEL_RESULTS]: Object.freeze({
    id: ROUTE_IDS.HOTEL_RESULTS,
    product: PRODUCT_IDS.AGENT,
    feature: "hotel_search",
    capability: "hotels",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchhotels",
      en: "hotels/results",
      el: null,
    }),
  }),

  [ROUTE_IDS.HOTEL_PROFILE]: Object.freeze({
    id: ROUTE_IDS.HOTEL_PROFILE,
    product: PRODUCT_IDS.AGENT,
    feature: "hotel_search",
    capability: "hotels",
    namespace: "hotels",
    paths: Object.freeze({
      he: "hotelprofile/:hotelId",
      en: "hotels/profile/:hotelId",
      el: "hotels/profile/:hotelId",
    }),
    legacyPaths: Object.freeze({
      he: "hotelprofile/:hotelId",
    }),
  }),

  [ROUTE_IDS.SEARCH_OFFER]: Object.freeze({
    id: ROUTE_IDS.SEARCH_OFFER,
    product: PRODUCT_IDS.AGENT,
    feature: "hotel_search",
    capability: "dashboard",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchoffer",
      en: "searchoffer",
      el: null,
    }),
  }),

  [ROUTE_IDS.HOTEL_OFFER]: Object.freeze({
    id: ROUTE_IDS.HOTEL_OFFER,
    product: PRODUCT_IDS.AGENT,
    feature: "hotel_search",
    capability: "hotels",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchoffer/:offerId",
      en: "hotels/offer/:offerId",
      el: "hotels/offer/:offerId",
    }),
    legacyPaths: Object.freeze({
      he: "searchoffer/:offerId",
    }),
  }),

  [ROUTE_IDS.FLIGHTS_SEARCH]: Object.freeze({
    id: ROUTE_IDS.FLIGHTS_SEARCH,
    product: PRODUCT_IDS.AGENT,
    feature: "flights",
    capability: "flights",
    namespace: "hotels",
    paths: Object.freeze({
      he: "flights/search",
      en: "flights/search",
      el: null,
    }),
  }),

  [ROUTE_IDS.SEARCH_PACKAGES]: Object.freeze({
    id: ROUTE_IDS.SEARCH_PACKAGES,
    product: PRODUCT_IDS.AGENT,
    feature: "packages",
    capability: "packages",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchpackages",
      en: "searchpackages",
      el: null,
    }),
  }),

  [ROUTE_IDS.SEARCH_DYNAMIC]: Object.freeze({
    id: ROUTE_IDS.SEARCH_DYNAMIC,
    product: PRODUCT_IDS.AGENT,
    feature: "packages",
    capability: "packages",
    namespace: "hotels",
    paths: Object.freeze({
      he: "searchdynamic",
      en: "searchdynamic",
      el: null,
    }),
  }),

  [ROUTE_IDS.BOOK_PACKAGE]: Object.freeze({
    id: ROUTE_IDS.BOOK_PACKAGE,
    product: PRODUCT_IDS.AGENT,
    feature: "booking",
    capability: "booking",
    namespace: "orders",
    paths: Object.freeze({
      he: "bookapackage",
      en: "bookapackage",
      el: null,
    }),
  }),

  [ROUTE_IDS.REMINDERS]: Object.freeze({
    id: ROUTE_IDS.REMINDERS,
    product: PRODUCT_IDS.AGENT,
    feature: "dashboard",
    capability: "dashboard",
    namespace: "dashboard",
    paths: Object.freeze({
      he: "remindes",
      en: "remindes",
      el: null,
    }),
  }),

  [ROUTE_IDS.AI_SALES_LEADS]: Object.freeze({
    id: ROUTE_IDS.AI_SALES_LEADS,
    product: PRODUCT_IDS.AGENT,
    feature: "dashboard",
    capability: "dashboard",
    namespace: "dashboard",
    paths: Object.freeze({
      he: "ai-sales-leads",
      en: "ai-sales-leads",
      el: null,
    }),
  }),

  [ROUTE_IDS.MANAGEMENT]: Object.freeze({
    id: ROUTE_IDS.MANAGEMENT,
    product: PRODUCT_IDS.AGENT,
    feature: "dashboard",
    capability: "dashboard",
    namespace: "dashboard",
    paths: Object.freeze({
      he: "management",
      en: "management",
      el: null,
    }),
  }),

  [ROUTE_IDS.ADMIN_PANEL]: Object.freeze({
    id: ROUTE_IDS.ADMIN_PANEL,
    product: PRODUCT_IDS.AGENT,
    feature: "admin",
    capability: "admin",
    namespace: "common",
    paths: Object.freeze({
      he: "admin",
      en: "admin",
      el: null,
    }),
  }),

  [ROUTE_IDS.SECURE_CLIENT_ROOT]: Object.freeze({
    id: ROUTE_IDS.SECURE_CLIENT_ROOT,
    product: PRODUCT_IDS.AGENT,
    feature: "auth",
    capability: "dashboard",
    namespace: "auth",
    paths: Object.freeze({
      he: null,
      en: null,
      el: null,
    }),
    absolutePaths: Object.freeze({
      he: "/p",
      en: "/p",
      el: "/p",
    }),
  }),

  [ROUTE_IDS.ADMINISTRATION]: Object.freeze({
    id: ROUTE_IDS.ADMINISTRATION,
    product: PRODUCT_IDS.ADMIN,
    feature: "admin",
    capability: "admin",
    namespace: "common",
    paths: Object.freeze({
      he: null,
      en: null,
      el: null,
    }),
    absolutePaths: Object.freeze({
      he: "/p/administration",
      en: "/p/administration",
      el: null,
    }),
  }),

  [ROUTE_IDS.ORDERS_LOBBY]: Object.freeze({
    id: ROUTE_IDS.ORDERS_LOBBY,
    product: PRODUCT_IDS.AGENT,
    feature: "orders",
    capability: "booking",
    namespace: "orders",
    paths: Object.freeze({
      he: "orders",
      en: "orders",
      el: null,
    }),
  }),

  [ROUTE_IDS.FINANCE]: Object.freeze({
    id: ROUTE_IDS.FINANCE,
    product: PRODUCT_IDS.AGENT,
    feature: "finance",
    capability: "payments",
    namespace: "orders",
    paths: Object.freeze({
      he: "finance",
      en: "finance",
      el: null,
    }),
  }),

  [ROUTE_IDS.PUBLIC_HOME]: Object.freeze({
    id: ROUTE_IDS.PUBLIC_HOME,
    product: PRODUCT_IDS.PUBLIC,
    feature: "public",
    capability: "dashboard",
    namespace: "common",
    paths: Object.freeze({
      he: "",
      en: "",
      el: "",
    }),
  }),

  [ROUTE_IDS.VACATION_PACKAGES]: Object.freeze({
    id: ROUTE_IDS.VACATION_PACKAGES,
    product: PRODUCT_IDS.PUBLIC,
    feature: "vacation_packages",
    capability: "hotels",
    namespace: "hotels",
    paths: Object.freeze({
      he: "vacation-packages",
      en: "vacation-packages",
      el: null,
    }),
  }),

  [ROUTE_IDS.ADMIN_HOME]: Object.freeze({
    id: ROUTE_IDS.ADMIN_HOME,
    product: PRODUCT_IDS.ADMIN,
    feature: "admin",
    capability: "admin",
    namespace: "common",
    paths: Object.freeze({
      he: "",
      en: "",
      el: null,
    }),
  }),
});

export function getRouteDefinition(routeId) {
  return ROUTE_REGISTRY[routeId] || null;
}

export function listRouteDefinitions() {
  return Object.values(ROUTE_REGISTRY);
}

/** SideNav / UpperNavBar menu segment → stable Route ID */
export const DASHBOARD_MENU_SEGMENT_TO_ROUTE_ID = Object.freeze({
  home: ROUTE_IDS.DASHBOARD_HOME,
  searchhotels: ROUTE_IDS.HOTEL_SEARCH,
  hotelprofile: ROUTE_IDS.HOTEL_PROFILE,
  "hotels/profile": ROUTE_IDS.HOTEL_PROFILE,
  searchoffer: ROUTE_IDS.SEARCH_OFFER,
  "hotels/offer": ROUTE_IDS.HOTEL_OFFER,
  "flights/search": ROUTE_IDS.FLIGHTS_SEARCH,
  searchpackages: ROUTE_IDS.SEARCH_PACKAGES,
  searchdynamic: ROUTE_IDS.SEARCH_DYNAMIC,
  bookapackage: ROUTE_IDS.BOOK_PACKAGE,
  orders: ROUTE_IDS.ORDERS_LOBBY,
  finance: ROUTE_IDS.FINANCE,
  management: ROUTE_IDS.MANAGEMENT,
  admin: ROUTE_IDS.ADMIN_PANEL,
  remindes: ROUTE_IDS.REMINDERS,
  "ai-sales-leads": ROUTE_IDS.AI_SALES_LEADS,
});
